import { useState, useEffect, useRef } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { 
  AlertTriangle, 
  Clock, 
  Lock, 
  MessageSquare, 
  RefreshCw, 
  Loader2,
  Copy,
  CheckCircle2,
  PartyPopper,
  CreditCard
} from 'lucide-react';
import { toast } from 'sonner';
import { useAuthStore } from '../store';

export default function BlockedPage() {
  const navigate = useNavigate();
  const location = useLocation();
  const { user: storeUser, checkAuth } = useAuthStore();
  const reason = location.state?.reason || 'blocked';
  
  const isExpired = reason === 'expired';
  
  // Estados para renovação
  const [canRenew, setCanRenew] = useState(false);
  const [renewalPrice, setRenewalPrice] = useState(null);
  const [loading, setLoading] = useState(false);
  const [generatingPix, setGeneratingPix] = useState(false);
  const [pixData, setPixData] = useState(null);
  const [paymentConfirmed, setPaymentConfirmed] = useState(false);
  const [currentUser, setCurrentUser] = useState(storeUser);
  
  // Polling
  const pollingRef = useRef(null);
  
  useEffect(() => {
    const init = async () => {
      // Garantir que temos os dados do usuário
      if (!currentUser) {
        const token = localStorage.getItem('nexus-token');
        if (token) {
          try {
            const res = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/auth/me`, {
              headers: { Authorization: `Bearer ${token}` }
            });
            if (res.ok) {
              const userData = await res.json();
              setCurrentUser(userData);
            }
          } catch (e) {
            console.error('Erro ao buscar dados do usuário:', e);
          }
        }
      }
      if (isExpired) {
        checkRenewalAvailability();
      }
    };
    init();
    
    return () => {
      if (pollingRef.current) {
        clearInterval(pollingRef.current);
      }
    };
  }, [isExpired]);
  
  const checkRenewalAvailability = async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem('nexus-token');
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/renewal-availability`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      if (response.ok) {
        const data = await response.json();
        setCanRenew(data.can_renew);
        setRenewalPrice(data.price || 30);
      }
    } catch (error) {
      console.error('Erro ao verificar renovação:', error);
    } finally {
      setLoading(false);
    }
  };
  
  const handleGeneratePix = async () => {
    try {
      setGeneratingPix(true);
      const token = localStorage.getItem('nexus-token');
      
      // Renovar própria conta
      const userId = currentUser?.id;
      if (!userId) {
        toast.error('Usuário não encontrado');
        return;
      }
      
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/renew/${userId}`, {
        method: 'POST',
        headers: { 
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });
      
      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.detail || 'Erro ao gerar PIX');
      }
      
      const data = await response.json();
      setPixData(data);
      toast.success('PIX gerado com sucesso!');
      
      // Iniciar polling para verificar pagamento
      startPaymentPolling(data.id);
      
    } catch (error) {
      toast.error(error.message);
    } finally {
      setGeneratingPix(false);
    }
  };
  
  const startPaymentPolling = (transactionId) => {
    if (pollingRef.current) {
      clearInterval(pollingRef.current);
    }
    
    // Verificar a cada 5 segundos
    pollingRef.current = setInterval(async () => {
      try {
        const token = localStorage.getItem('nexus-token');
        const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/transactions`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        
        if (response.ok) {
          const transactions = await response.json();
          const tx = transactions.find(t => t.id === transactionId);
          
          if (tx?.status === 'approved') {
            setPaymentConfirmed(true);
            clearInterval(pollingRef.current);
            toast.success('Pagamento confirmado! Sua conta foi renovada.');
            
            // Aguardar 3 segundos e redirecionar
            setTimeout(() => {
              window.location.href = '/';
            }, 3000);
          } else if (tx?.status === 'cancelled' || tx?.status === 'expired') {
            clearInterval(pollingRef.current);
            setPixData(null);
            toast.error('Pagamento cancelado ou expirado');
          }
        }
      } catch (error) {
        console.error('Erro ao verificar pagamento:', error);
      }
    }, 5000);
  };
  
  const handleCopyPix = () => {
    if (pixData?.qr_code_text) {
      navigator.clipboard.writeText(pixData.qr_code_text);
      toast.success('Código PIX copiado!');
    }
  };
  
  const formatCurrency = (value) => {
    return new Intl.NumberFormat('pt-BR', {
      style: 'currency',
      currency: 'BRL'
    }).format(value);
  };
  
  // Tela de pagamento confirmado
  if (paymentConfirmed) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center p-4">
        <div className="fixed inset-0 overflow-hidden pointer-events-none">
          <div className="absolute top-1/4 left-1/4 w-96 h-96 bg-green-500/10 rounded-full blur-3xl animate-pulse" />
          <div className="absolute bottom-1/4 right-1/4 w-96 h-96 bg-green-500/5 rounded-full blur-3xl animate-pulse delay-1000" />
        </div>
        
        <Card className="w-full max-w-sm glass-card border-green-500/20">
          <CardContent className="p-8 text-center">
            <div className="w-20 h-20 rounded-full bg-green-500/20 mx-auto mb-4 flex items-center justify-center">
              <PartyPopper className="w-10 h-10 text-green-500" />
            </div>
            <h2 className="font-heading font-bold text-2xl text-green-500 mb-2">
              Pagamento Confirmado!
            </h2>
            <p className="text-muted-foreground mb-4">
              Sua conta foi renovada com sucesso.
            </p>
            <p className="text-sm text-muted-foreground">
              Redirecionando para o painel...
            </p>
            <Loader2 className="w-6 h-6 animate-spin mx-auto mt-4 text-green-500" />
          </CardContent>
        </Card>
      </div>
    );
  }
  
  return (
    <div className="min-h-screen bg-background flex items-center justify-center p-4">
      {/* Background Effects */}
      <div className="fixed inset-0 overflow-hidden pointer-events-none">
        <div className="absolute top-1/4 left-1/4 w-96 h-96 bg-destructive/10 rounded-full blur-3xl animate-pulse" />
        <div className="absolute bottom-1/4 right-1/4 w-96 h-96 bg-destructive/5 rounded-full blur-3xl animate-pulse delay-1000" />
      </div>

      <div className="w-full max-w-sm relative z-10">
        {/* Logo */}
        <div className="text-center mb-8">
          <div className="w-16 h-16 rounded-2xl bg-destructive/20 mx-auto mb-4 flex items-center justify-center">
            {isExpired ? (
              <Clock className="w-8 h-8 text-destructive" />
            ) : (
              <Lock className="w-8 h-8 text-destructive" />
            )}
          </div>
          <h1 className="font-heading font-bold text-3xl text-foreground">NEXUZAP</h1>
          <p className="text-destructive font-mono text-sm mt-1">ACESSO RESTRITO</p>
        </div>

        {/* Card */}
        <Card className="glass-card border-destructive/20 animate-fade-in">
          <CardContent className="p-6">
            {!pixData ? (
              // Tela inicial
              <div className="text-center">
                <div className="w-16 h-16 rounded-full bg-destructive/10 mx-auto mb-4 flex items-center justify-center">
                  <AlertTriangle className="w-8 h-8 text-destructive" />
                </div>
                
                <h2 className="font-heading font-bold text-xl text-foreground mb-2">
                  {isExpired ? 'Conta Expirada' : 'Conta Bloqueada'}
                </h2>
                
                <p className="text-muted-foreground text-sm mb-6">
                  {isExpired 
                    ? 'Seu período de acesso expirou. Renove agora para continuar usando o sistema.'
                    : 'Sua conta está bloqueada. Entre em contato com seu administrador para mais informações.'
                  }
                </p>

                {/* Botão de Renovar */}
                {isExpired && canRenew && (
                  <div className="space-y-4 mb-6">
                    <div className="p-4 bg-primary/10 rounded-lg border border-primary/20">
                      <p className="text-sm text-muted-foreground mb-2">Valor da renovação</p>
                      <p className="text-3xl font-bold text-primary">
                        {formatCurrency(renewalPrice || 30)}
                      </p>
                      <p className="text-xs text-muted-foreground mt-1">por 30 dias</p>
                    </div>
                    
                    <Button 
                      className="w-full h-12 text-lg"
                      onClick={handleGeneratePix}
                      disabled={generatingPix}
                    >
                      {generatingPix ? (
                        <>
                          <Loader2 className="w-5 h-5 mr-2 animate-spin" />
                          Gerando PIX...
                        </>
                      ) : (
                        <>
                          <CreditCard className="w-5 h-5 mr-2" />
                          Renovar Agora
                        </>
                      )}
                    </Button>
                  </div>
                )}

                {isExpired && !canRenew && !loading && (
                  <div className="p-4 bg-muted/30 rounded-lg text-left mb-6">
                    <h3 className="text-sm font-medium text-foreground mb-2">O que fazer?</h3>
                    <ul className="text-xs text-muted-foreground space-y-1">
                      <li>• Entre em contato com seu administrador</li>
                      <li>• Solicite a renovação da sua conta</li>
                      <li>• Aguarde a liberação do acesso</li>
                    </ul>
                  </div>
                )}

                {!isExpired && (
                  <div className="p-4 bg-muted/30 rounded-lg text-left mb-6">
                    <h3 className="text-sm font-medium text-foreground mb-2">O que fazer?</h3>
                    <ul className="text-xs text-muted-foreground space-y-1">
                      <li>• Entre em contato com seu administrador</li>
                      <li>• Solicite o desbloqueio da sua conta</li>
                      <li>• Aguarde a liberação do acesso</li>
                    </ul>
                  </div>
                )}

                <Button
                  onClick={() => {
                    localStorage.removeItem('nexus-token');
                    navigate('/login');
                  }}
                  variant="outline"
                  className="w-full border-border text-foreground"
                >
                  Voltar ao Login
                </Button>
              </div>
            ) : (
              // Tela com QR Code
              <div className="space-y-4">
                <div className="text-center">
                  <Badge className="mb-4 bg-primary/20 text-primary border-primary/30">
                    PIX Gerado
                  </Badge>
                  <p className="text-sm text-muted-foreground mb-3">
                    Escaneie o QR Code ou copie o código PIX
                  </p>
                  <div className="bg-white rounded-xl p-4 inline-block">
                    <img 
                      src={pixData.qr_code ? (pixData.qr_code.startsWith('http') ? pixData.qr_code : pixData.qr_code.startsWith('data:') ? pixData.qr_code : `data:image/png;base64,${pixData.qr_code}`) : (pixData.qr_code_url || (pixData.qr_code_text ? `https://api.qrserver.com/v1/create-qr-code/?size=300x300&data=${encodeURIComponent(pixData.qr_code_text)}` : ''))}
                      alt="QR Code PIX"
                      className="w-48 h-48"
                    />
                  </div>
                </div>
                
                {/* Valor */}
                <div className="text-center py-2">
                  <p className="text-sm text-muted-foreground">Valor</p>
                  <p className="text-2xl font-bold text-primary">
                    {formatCurrency(pixData.amount)}
                  </p>
                </div>
                
                {/* Código copia e cola */}
                <div className="space-y-2">
                  <p className="text-sm text-muted-foreground text-center">PIX Copia e Cola</p>
                  <div className="relative">
                    <input
                      type="text"
                      value={pixData.qr_code_text}
                      readOnly
                      className="w-full p-3 pr-12 bg-muted rounded-lg text-xs font-mono truncate"
                    />
                    <Button
                      size="icon"
                      variant="ghost"
                      className="absolute right-1 top-1/2 -translate-y-1/2"
                      onClick={handleCopyPix}
                    >
                      <Copy className="w-4 h-4" />
                    </Button>
                  </div>
                </div>
                
                {/* Status do pagamento */}
                <div className="flex items-center justify-center gap-2 text-sm text-muted-foreground py-2">
                  <Loader2 className="w-4 h-4 animate-spin" />
                  Aguardando pagamento...
                </div>
                
                {/* Timer de expiração */}
                <p className="text-xs text-center text-muted-foreground">
                  Este PIX expira em 30 minutos
                </p>
                
                {/* Botão para gerar novo */}
                <Button
                  onClick={() => setPixData(null)}
                  variant="outline"
                  className="w-full"
                >
                  Cancelar e voltar
                </Button>
              </div>
            )}
          </CardContent>
        </Card>

        {/* Footer */}
        <div className="text-center mt-6">
          <div className="flex items-center justify-center gap-2 text-muted-foreground">
            <MessageSquare className="w-4 h-4" />
            <span className="text-xs">NexuZap - Gestao de Clientes</span>
          </div>
        </div>
      </div>
    </div>
  );
}
